import React, { useState, useRef, useContext, useEffect, useCallback } from "react";
import {
  Box,
  CircularProgress,
  Button,
  Typography,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { Field, Formik, Form } from "formik";
import * as yup from "yup";
import { TextField } from "formik-material-ui";
import "../../utils/css/UserBankDetails.css";
import AutoPayBankPopup from "./AutoPayBankPopup";
import { Context } from "../../../context/DataProvider";
import axiosInstance from "../../../utils/axiosInterceptor";
import loggingService from "../../../services/logging.service";
import { FileUploader, isSafeFilename ,checkHarmfulPdfContent} from "../../utils/fileUploader";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import uploadIcon from "../../utils/images/upload-04.svg";
import { LocalStorageKeys } from "../../utils/constants/constants";
import { ACCESSIBILITY_LABELS } from "../../../config/constants";
import { handleKeyboardClick } from "../../../utils/keyboardUtils";
import BankDocumentPreview from "./BankDocumentPreview";
import BankDocumentPreviewModal from "./BankDocumentPreviewModal";
import DeleteIcon from "../../utils/images/delete-02.svg";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  shape: {
    borderRadius: 280,
  },

  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const BootstrapDialogTitle = (props) => {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle
      sx={{
        m: 0,
        p: 3,
        paddingBottom: 2,
        paddingRight: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
      {...other}
    >
      {children}
      {onClose ? (
        <IconButton
          aria-label={ACCESSIBILITY_LABELS.BANK_DETAILS.CLOSE_DIALOG}
          onClick={onClose}
          sx={{
            color: "#5F6368",
            width: 36,
            height: 36,
          }}
        >
          <CloseIcon sx={{ fontSize: 26 }} />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

const BorderCard = styled("div")`
  box-sizing: border-box;
  border-radius: 15px;
  width: 276px;
  max-width: 100%;
  margin: 5px;
  position: relative;
  border: ${(props) =>
    props.selected ? "1.5px solid #1C6EF2" : "1px solid #d5dde3"};
  outline: none;
  &:focus {
    outline: none;
  }
  @media (max-width: 600px) {
  width: 280px;
}

`;

const BankCard = ({ option, selected, onChange, index, onIrPopUpClickOpen }) => {
  const itemRef = useRef();
  const tagLabel =
    option.isverified === 1
      ? "Verified"
      : option.isverified === 2
      ? "Info Required"
      : option.isverified === 3
      ? "Info Submitted"
      : "Info Submitted";

  return (
    <BorderCard
      selected={selected}
      role={ACCESSIBILITY_LABELS.ROLES.OPTION}
      tabIndex="0"
      sx={{
        width: "350px",
        height: "200px",
      }}
      onClick={(e) => {
        onChange();
        // Move focus to next element after selection
        setTimeout(() => {
          const nextElement = document.querySelector('[data-focus-after-bank-selection="true"]');
          if (nextElement) {
            nextElement.focus();
          }
        }, 0);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onChange();
          // Move focus to next element after selection
          setTimeout(() => {
            const nextElement = document.querySelector('[data-focus-after-bank-selection="true"]');
            if (nextElement) {
              nextElement.focus();
            }
          }, 0);
        }
      }}
      aria-selected={selected}
      aria-label={ACCESSIBILITY_LABELS.BANK_DETAILS.BANK_CARD(option.bankname, tagLabel, option.accountholdername || option.benef_name_bank_returned, option.accountholdernumber, option.ifsccode, option.branchname, selected)}
      ref={itemRef}
    >
      <Box
        className="bankCardDiv"
        aria-hidden="true"
        onClick={() =>
          option.isverified === 2 ? onIrPopUpClickOpen(index) : ""
        }
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            padding: "6px 12px",
            borderTopRightRadius: "15px",
            borderBottomLeftRadius: "12px",
            fontSize: "10px",
            fontWeight: 600,
            backgroundColor:
              option.isverified === 1
                ? "#F1F8F5"
                : option.isverified === 2
                ? "#FFF5ED"
                : "#EBEEF1",
            color:
              option.isverified === 1
                ? "#26935F"
                : option.isverified === 2
                ? "#CD6300"
                : "#252D38",
          }}
        >
          {tagLabel}
        </Box>
        <div
          className="Bank_Name"
          style={{ paddingBottom: "10px" }}
        >
          {option.bankname ? option.bankname : "BANK DETAILS"}
        </div>
        <div key="bankname" className="p-b-5 bankDetails">
          Account Holder : {option.accountholdername || option.benef_name_bank_returned}
        </div>
        <div key="bankaccno" className="p-b-5 bankDetails">
          Account Number : {option.accountholdernumber}
        </div>
        <div key="bankiifsccode" className="p-b-5 bankDetails">
          IFSC Code : {option.ifsccode}
        </div>
        <div key="bankbranch" className="p-b-5 bankDetails">
          {option.branchname ? <> Branch : {option.branchname} </> : ""}
        </div>
      </Box>
    </BorderCard>
  );
};

function BankDetailsForm(props) {
  const [open, setOpen] = React.useState(false);
  const [autoPayOpen, setAutoPayOpen] = React.useState(false);
  const isPennyDropEnabled = localStorage.getItem(LocalStorageKeys.PENNY_DROP_ENABLED) === "true";
  const [isIRBank, setIsIRBank] = React.useState(false);
  const { bankSelected, setBankSelected } = useContext(Context);
  let apiUrl = process.env.REACT_APP_REIMBURSEMENT_URL;
  const [bankFile, setBankFileState] = useState([]);
  let l = props.bank.length;
  const fileRef = useRef();
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [successToast, setSuccessToast] = useState({ show: false, message: "", key: 0 });
  const [previewModal, setPreviewModal] = useState({
    open: false,
    url: null,
    isPdf: false,
    name: "",
  });

  useEffect(() => {
    if (successToast.show) {
      const timer = setTimeout(() => {
        setSuccessToast({ show: false, message: "", key: 0 });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successToast.show, successToast.key]);

  const handleClickOpen = () => {
    if (isPennyDropEnabled && props.bank.length < 10) {
      setAutoPayOpen(true);
    } else {
      setOpen(true);
    }
  };
  const handleClose = () => {
    setOpen(false);
    props.loadBankDetails();
  };

  const handleIrPopUpClickOpen = (index) => {
    setBankSelected(index);
    setIsIRBank(true);
    setOpen(true);
  };



const checkScrollButtons = useCallback(() => {
  if (scrollContainerRef.current) {
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    const hasScrollLeft = scrollLeft > 0;
    const hasScrollRight = scrollLeft < scrollWidth - clientWidth - 10;

    if (canScrollLeft !== hasScrollLeft) {
      setCanScrollLeft(hasScrollLeft);
    }
    if (canScrollRight !== hasScrollRight) {
      setCanScrollRight(hasScrollRight);
    }
  }
}, [canScrollLeft, canScrollRight]);

useEffect(() => {
  const container = scrollContainerRef.current;
  if (!container) return;
  checkScrollButtons();

  const scrollHandler = () => {
    checkScrollButtons();
  };

  container.addEventListener("scroll", scrollHandler, { passive: true });
  window.addEventListener("resize", checkScrollButtons, { passive: true });

  return () => {
    container.removeEventListener("scroll", scrollHandler);
    window.removeEventListener("resize", checkScrollButtons);
  };
}, [props.bank.length, checkScrollButtons]);

const scrollLeft = useCallback(() => {
  if (scrollContainerRef.current) {
    scrollContainerRef.current.scrollBy({ left: -300, behavior: "smooth" });
  }
}, []);

const scrollRight = useCallback(() => {
  if (scrollContainerRef.current) {
    scrollContainerRef.current.scrollBy({ left: 300, behavior: "smooth" });
  }
}, []);

  const handlePreview = useCallback((fileObject, previewUrl, isPdf, fileName) => {
    let url = previewUrl;
    if (isPdf && fileObject instanceof Blob) {
      try {
        url = URL.createObjectURL(fileObject);
      } catch (error) {
        console.error("Error creating PDF URL:", error);
        return;
      }
    }
    if (url) {
      setPreviewModal({
        open: true,
        url: url,
        isPdf: isPdf,
        name: fileName,
      });
    }
  }, []);

  const handleDeleteDocument = useCallback(() => {
    setBankFileState([]);
    setfileupload("");
    if (fileRef.current) {
      fileRef.current.value = "";
    }
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewModal((prev) => {
      if (prev.url && prev.isPdf) {
        URL.revokeObjectURL(prev.url);
      }
      return {
        open: false,
        url: null,
        isPdf: false,
        name: "",
      };
    });
  }, []);

  const addBankDetails = async (values, isIRUpdated) => {
    let funRes;
    let formdata = new FormData();
    var bankDocname = bankFile[0].name.split(".");
    var today = new Date();
    var dd = String(today.getDate()).padStart(2, "0");
    var mm = String(today.getMonth() + 1).padStart(2, "0");
    var yyyy = today.getFullYear();
    var date = new Date();
    var hour = date.getHours();
    var min = date.getMinutes();
    var sec = date.getSeconds();
    var milli = date.getMilliseconds();
    today =
      yyyy +
      "_" +
      mm +
      "_" +
      dd +
      "_" +
      hour +
      "_" +
      min +
      "_" +
      sec +
      "_" +
      milli;
    formdata.append(
      "file",
      bankFile[0],
      "PAT" +
        "_" +
        props.users.mbUserId +
        "_" +
        Math.random().toString(16).slice(2) +
        "_" +
        today +
        "." +
        bankDocname[bankDocname.length - 1]
    );
    let fileName =
      "PAT" +
      "_" +
      props.users.mbUserId +
      "_" +
      Math.random().toString(16).slice(2) +
      "_" +
      today +
      "." +
      bankDocname[bankDocname.length - 1];
    let fileType = "BankDocuments";
    const config = {
      headers: {
        authtoken: props.accessToken,
      },
    };

    const newBank = {
      ifsccode: values.IFSCcode,
      branchname: values.BranchName,
      accountholdername: values.ACHolderName,
      accountholdernumber: values.AccountNo,
      bankname: values.Bankname,
    };
    let addBankResponse, uploadBankDocResponse;

    uploadBankDocResponse = {
      isSuccess: false,
      Key: `${fileType}/${fileName}`,
    };
    let imageType = `image/${fileName.split(".").pop()}`;
    let singleFiledata = new File([bankFile[0]], fileName, { type: imageType });
    try {
      // uploadBankDocResponse = await axios.post(
      // 	`${apiUrl}/auth/uploadBankDocument`,
      // 	formdata,
      // 	config
      // );
      const fileUploadResponse = await FileUploader.uploadFileData(
        props?.users?.pmEntityId,
        singleFiledata,
        fileType
      );
      uploadBankDocResponse.isSuccess = fileUploadResponse.isSuccess;
      uploadBankDocResponse.Key = fileUploadResponse.fileName;
    } catch (error) {
      loggingService.log({
        level: "ERROR",
        method: "POST/PUT",
        message: `Error in uplodaing File :  ${error.message}`,
        error: error,
      });
      if (error.response.data.message === "Token_Expired")
        funRes = "Token_Expired";
      else funRes = "failure";
      return funRes;
    }

    if (uploadBankDocResponse.isSuccess) {
      const addBankBody = {
        ifscCode: values.IFSCcode,
        branchName: values.BranchName,
        acholderName: values.ACHolderName,
        accNo: values.AccountNo,
        bankName: values.Bankname,
        document: uploadBankDocResponse.Key,
        oldaccNo: bankSelected
          ? props.bank[bankSelected].accountholdernumber
          : "",
        isupdationflag: bankSelected
          ? props.bank[bankSelected].accountholdernumber == values.AccountNo
            ? false
            : true
          : null,
        irflag: isIRUpdated,
      };

      try {
        addBankResponse = await axiosInstance.post(
          `${apiUrl}/auth/addUserBankDetails`,
          addBankBody,
          config
        );
      } catch (error) {
        if (error.response.data.message === "Token_Expired")
          funRes = "Token_Expired";
        else funRes = "failure";
        return funRes;
      }
    }

    if (
      !addBankResponse.data.isSuccess &&
      addBankResponse.data.message === "already present"
    ) {
      funRes = addBankResponse.data.message;
    } else if (
      !uploadBankDocResponse.isSuccess ||
      !addBankResponse.data.isSuccess
    ) {
      funRes = "failure";
    } else {
      funRes = "success";
      if (isIRUpdated) props.bank[bankSelected] = newBank;
      else props.bank[l] = newBank;
    }
    return funRes;
  };


  const addBankDocuments = async (bankDocument) => {
    const filesArray = Array.from(bankDocument || []);
    setBankFileState([]);

    if (!filesArray.length) {
      setfileupload("Please upload bank document");
      return;
    }

    let documentSize = 0;
    let documentType = false;
    let zerobyteflag = 0;
    for (var i = 0; i < filesArray.length; ++i) {
      if (filesArray[i].size === 0) {
        zerobyteflag = 1;
        break;
      }
      
      documentSize += filesArray[i].size / 1000;

      if (!isSafeFilename(filesArray[i].name)) {
        setfileupload("File name contains unsupported characters. Please use letters, numbers, or basic symbols only.");
        return;
      }

      if (
        !(
          (filesArray[i].type === "application/pdf") |
          (filesArray[i].type === "image/png") |
          (filesArray[i].type === "image/jpg") |
          (filesArray[i].type === "image/jpeg")
        )
      )
        documentType = true;
    }

    try {
      const isPdfSafe = await checkHarmfulPdfContent(filesArray);
      if (!isPdfSafe) {
        setfileupload("Invalid or harmful file detected. Please upload valid PDF files only.");
        return;
      }
    } catch (error) {
      setfileupload("Error validating PDF content. Please try again.");
      return;
    }

    if (zerobyteflag) {
      setfileupload("Can't upload zero byte file");
    } else if (documentType) {
      setfileupload("Upload Only Image or PDF");
    } else if (documentSize > 10000) {
      setfileupload("File Size is beyond 50MB");
    } else {
      setfileupload("");
      setBankFileState(filesArray);
    }

  };

  

  const [fileupload, setfileupload] = useState();
  return (
    <>
      {successToast.show && (
        <Box
          sx={{
            position: "fixed",
            top: { xs: "auto", md: "20px" },
            bottom: { xs: "80px", md: "auto" },
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1400,
            backgroundColor: "#252D38",
            color: "#FFFFFF",
            borderRadius: "8px",
            padding: { xs: "12px 16px", md: "10px 20px" },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            minWidth: { xs: "90%", md: "auto" },
            maxWidth: { xs: "90%", md: "600px" },
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "14px", md: "14px" },
              fontWeight: 500,
              color: "#FFFFFF",
              lineHeight: 1.4,
            }}
          >
            {successToast.message}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setSuccessToast({ show: false, message: "" })}
            sx={{
              color: "#FFFFFF",
              padding: "2px",
              flexShrink: 0,
            }}
          >
            <CloseIcon sx={{ fontSize: "18px" }} />
          </IconButton>
        </Box>
      )}
      {props.bankFlag === 1 ? (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "space-between", md: "space-between" },
              alignItems: "center",
              marginBottom: { xs: 1, sm: 1 , md: 2 },
            }}
          >
            <Box sx={{ display: { xs: "contents", md: "flex" }, alignItems: { md: "center" }, gap: { md: 2 } }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: {xs: '18px', md: '23px'},
                  color: "#252D38",
                }}
              >
                Bank Details
              </Typography>
              <Box
                style={{ textAlign: "center" }}
                onClick={handleClickOpen}
                role={ACCESSIBILITY_LABELS.ROLES.BUTTON}
                aria-label={ACCESSIBILITY_LABELS.BANK_DETAILS.ADD_BANK_ACCOUNT}
                tabIndex="0"
                onKeyDown={handleKeyboardClick(handleClickOpen)}
              >
                <Box
                  sx={{
                    padding: "8px",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: { xs: "space-between", md: "center" },
                    alignItems: "center",
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    borderColor: "#72B3FF",
                    border: { xs: "none", md: "1px solid #72B3FF" },
                    boxShadow: { xs: "none", md: "0 1px 2px 0 rgba(23, 27, 34, 0.05)" },
                    width: { xs: "120px", sm: "160px" },
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      boxShadow: { xs: "none", md: "0 2px 6px rgba(23, 27, 34, 0.15)" },
                    },
                  }}
                >
                  <AddIcon sx={{ color: "#0066DC", fontSize: "20px", marginRight: "5px", display: { xs: "block", md: "none" } }} />
                  <Typography
                    variant="body2"
                    color="#0066DC"
                    style={{ fontSize: "16px",whiteSpace: "nowrap" }}
                  >
                      Add Account
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                gap: 1,
              }}
            >
              <IconButton
                onClick={scrollLeft}
                disabled={!canScrollLeft}
                tabIndex="-1"
                aria-label={ACCESSIBILITY_LABELS.BANK_DETAILS.SCROLL_LEFT}
                sx={{
                  backgroundColor: "#F0F3F5",
                  borderRadius: "30px",
                  width: 36,
                  height: 36,
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#f9f9f9",
                    opacity: 0.5,
                  },
                }}
              >
                <ArrowBackIcon
                  aria-hidden="true"
                  sx={{
                    color: canScrollLeft ? "#2E3742" : "#BCC6D1",
                    fontSize: 22,
                  }}
                />
              </IconButton>

              <IconButton
                onClick={scrollRight}
                disabled={!canScrollRight}
                tabIndex="-1"
                aria-label={ACCESSIBILITY_LABELS.BANK_DETAILS.SCROLL_RIGHT}
                sx={{
                  backgroundColor: "#F0F3F5",
                  borderRadius: "30px",
                  width: 36,
                  height: 36,
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#f9f9f9",
                    opacity: 0.5,
                  },
                }}
              >
                <ArrowForwardIcon
                  aria-hidden="true"
                  sx={{
                    color: canScrollRight ? "#2E3742" : "#BCC6D1",
                    fontSize: 22,
                  }}
                />
              </IconButton>
            </Box>
          </Box>
        
          <div className="flex flex-wrap">
            <Box sx={{ position: "relative", width: "100%" }}>
              <Box
                ref={scrollContainerRef}
                sx={{
                  display: "flex",
                  overflowX: "auto",
                  gap: 2,
                  paddingBottom: 2,
                  paddingTop:1 ,
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  msOverflowStyle: "none",
                }}
                role={ACCESSIBILITY_LABELS.ROLES.LISTBOX}
                aria-label={`Bank accounts. ${props.bank?.length || 0} account${props.bank?.length !== 1 ? "s" : ""} available. Select one to receive reimbursement.`}
              >
                {props.bank && props.bank.length > 0 ? (
                  props.bank.map((option, index) => (
                    <Box key={index} sx={{ flexShrink: 0 }}>
                      <Box
                        className="min-width-250 border-radius-10"
                        style={{
                          border: "5px",
                          borderColor: "black",
                          fontSize: "14px",
                        }}
                        key="bankcard"
                      >
                        <BankCard
                          key={index}
                          index={index}
                          option={option}
                          selected={Boolean(
                            bankSelected === index &&
                              props.bank[index].isverified !== 2
                          )}
                          onChange={() => {
                            setBankSelected(index);
                            props.onSelect(index);
                          }}
                          onIrPopUpClickOpen={handleIrPopUpClickOpen}
                        />
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      backgroundColor: "#F5F7F8",
                      borderRadius: "8px",
                      padding: {
                        xs: "30px",
                        md: "70px",
                      },
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "18px",
                        fontWeight: 500,
                        color: "#47566A",
                        marginBottom: "8px",
                      }}
                    >
                      No bank account added yet
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: 400,
                        color: "#47566A",
                      }}
                    >
                      Add your bank account to receive reimbursements and complete
                      transactions smoothly.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </div>
          <BootstrapDialog
            onClose={handleClose}
            aria-labelledby="customized-dialog-title"
            aria-modal="true"
            open={open}
            fullWidth
            maxWidth="lg"
            PaperProps={{
              sx: {
                width: { xs: "95%", sm: "850px", md: "1100px" },
                maxWidth: "98%",
                height: "auto",
                minHeight: "300px",
                maxHeight: "90vh",
                borderRadius: "10px",
                padding: { xs: "6px", sm: "8px", md: "14px" },
                display: "flex",
                flexDirection: "column",
                scrollbarWidth: "thin",
              },
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "auto",
              }}
            >
              <BootstrapDialogTitle
                id="customized-dialog-title"
                onClose={handleClose}
                style={{
                  fontSize: "21px",
                  fontWeight: "500",
                  color: "#252D38",
                  flexShrink: 0,
                  paddingBottom: "12px",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "21px",
                      fontWeight: 500,
                      color: "#252D38",
                    }}
                  >
                    {isIRBank
                      ? "Update Bank Account Details"
                      : "Bank Account Details"}
                  </Typography>

                  {isIRBank && props.bank[bankSelected].remarks ? (
                    <Typography
                      sx={{
                        fontSize: "14px",
                      }}
                    >
                      Your previously submitted bank details/proofs were
                      rejected due to {props.bank[bankSelected].remarks}.
                    </Typography>
                  ) : null}
                </Box>
              </BootstrapDialogTitle>
              <Formik
                enableReinitialize={false}
                initialValues={{
                  AccountNo: isIRBank
                    ? props.bank[bankSelected].accountholdernumber
                    : "",
                  ReAccountNo: isIRBank
                    ? props.bank[bankSelected].accountholdernumber
                    : "",
                  Bankname: isIRBank ? props.bank[bankSelected].bankname : "",
                  IFSCcode: isIRBank ? props.bank[bankSelected].ifsccode : "",
                  BranchName: isIRBank
                    ? props.bank[bankSelected].branchname
                    : "",
                  ACHolderName: isIRBank
                    ? props.bank[bankSelected].accountholdername || props.bank[bankSelected].benef_name_bank_returned
                    : "",
                  file: null,
                }}
                onSubmit={async (values) => {
                  let res = await addBankDetails(values, isIRBank);
                  if (res === "already present") {
                    setfileupload(
                      "You are trying to add an already existing bank account with account number " +
                        values.AccountNo +
                        ". Please try adding a new bank account."
                    );
                  } else if (res === "failure") {
                    setfileupload(
                      "Failed to add bank account. Please try again"
                    );
                  } else if (res === "Token_Expired") {
                    setfileupload("Session expired please login again");
                  } else {
                    setfileupload();
                    handleClose();
                  }
                }}
                validationSchema={yup.object().shape({
                  AccountNo: yup
                    .string()
                    .required("Account Number is Required")
                    .matches(/^[0-9]+$/, "Account number must be in numbers")
                    .min(5, "Must be min 5 digits")
                    .max(18, "Cannot exceed 18 digits"),
                  ReAccountNo: yup.string().when("AccountNo", {
                    is: (val) => val && val.length > 0,
                    then: (schema) =>
                      schema
                        .required("Re-enter Account Number is Required")
                        .oneOf(
                          [yup.ref("AccountNo")],
                          "Account number didn't match"
                        ),
                    otherwise: (schema) => schema.notRequired(),
                  }),
                  Bankname: yup
                    .string()
                    .required("Bank name is Required")
                    .matches(
                      /^[A-Za-z ]+$/,
                      "Bank name cannot contain numbers and special characters"
                    ),
                  IFSCcode: yup
                    .string()
                    .required("IFSC Code Is Required")
                    .test(
                      "IFSC Code",
                      "IFSC Code must be of length 11",
                      (value) => value?.length === 11
                    )
                    .matches(
                      /^[A-Z]{4}0[A-Z0-9]{6}$/,
                      "IFSC code must be 4 uppercase letters, followed by 0, then 6 alphanumeric characters"
                    ),
                  BranchName: yup
                    .string()
                    .required("Branch name is Required")
                    .matches(
                      /^[A-Za-z ]+$/,
                      "Branch name cannot contain numbers and special characters"
                    ),
                  ACHolderName: yup
                    .string()
                    .required("AC Holder name is Required")
                    .matches(
                      /^[A-Za-z ]+$/,
                      "AC Holder name cannot contain numbers and special characters"
                    ),
                })}
                validateOnChange={true}
                validateOnBlur={true}
              >
                {({
                  isSubmitting,
                  values,
                  setFieldValue,
                  submitForm,
                  isValid,
                  errors,
                  touched,
                }) => (
                  <>
                    <Box
                      sx={{
                        flex: 1,
                        overflowY: "scroll",
                        overflowX: "hidden",
                        minHeight: 0,
                        "&::-webkit-scrollbar": {
                          width: "6px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          background: "#888",
                          borderRadius: "3px",
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                          background: "#555",
                        },
                        scrollbarWidth: "thin",
                      }}
                    >
                      <Box
                        role="note"
                        tabIndex="0"
                        aria-label="Important information about bank account details. Please ensure the following: 1. The bank details should only belong to the employee and not any of his dependents. 2. The bank account proof should have Bank Account Holder Name, IFSC Code and Bank Account number mandatorily."
                        sx={{
                          backgroundColor: "#FFFAF6",
                          border: "1px solid #FEEBDA",
                          borderRadius: "8px",
                          padding: "16px",
                          margin: {
                            xs: "12px 16px",
                            sm: "14px 20px",
                            md: "16px 24px",
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 500,
                            color: "#252D38",
                            fontSize: "16px",
                            marginBottom: 1,
                          }}
                        >
                          Please ensure the following with respect to the bank
                          account details:
                        </Typography>
                        <Box
                          component="ul"
                          sx={{ margin: 0, padding: 0, listStyle: "none" }}
                        >
                          <Box
                            component="li"
                            sx={{
                              fontSize: "14px",
                              color: "#252D38",
                              marginBottom: 1,
                              paddingLeft: "20px",
                              position: "relative",
                              "&:before": {
                                content: '"1."',
                                position: "absolute",
                                left: 0,
                                fontWeight: 400,
                              },
                            }}
                          >
                            The bank details should only belong to the employee
                            and not any of his dependents
                          </Box>
                          <Box
                            component="li"
                            sx={{
                              fontSize: "14px",
                              color: "#252D38",
                              paddingLeft: "20px",
                              position: "relative",
                              "&:before": {
                                content: '"2."',
                                position: "absolute",
                                left: 0,
                                fontWeight: 400,
                              },
                            }}
                          >
                            The bank account proof (passbook/statement/cheque
                            book) should have Bank Account Holder Name, IFSC
                            Code and Bank Account number mandatorily.
                          </Box>
                        </Box>
                      </Box>
                      <DialogContent
                        sx={{
                          padding: {
                            xs: "8px 16px",
                            sm: "12px 20px",
                            md: "16px 24px",
                          },
                        }}
                      >
                        <Form autoComplete="off" id="bank-details-form">
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                              <Field
                                style={{
                                  width: "100%",
                                  color: "#47566A",
                                  fontSize: "16px",
                                  fontWeight: "300",
                                }}
                                component={TextField}
                                id="AccountNo"
                                name="AccountNo"
                                label="Enter Account No."
                                type="password"
                                inputProps={{
                                  "aria-label": "Enter Account Number. Enter each digit one by one."
                                }}
                                onPaste={(e) => {
                                  e.preventDefault();
                                  return false;
                                }}
                                onCopy={(e) => {
                                  e.preventDefault();
                                  return false;
                                }}
                              >
                                Enter Account No{" "}
                              </Field>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Field
                                style={{
                                  width: "100%",
                                  color: "#47566A",
                                  fontSize: "16px",
                                  fontWeight: "300",
                                }}
                                component={TextField}
                                id="ReAccountNo"
                                name="ReAccountNo"
                                label="Re-enter Account No."
                                disabled={
                                  values.AccountNo === "" ? true : false
                                }
                                inputProps={{
                                  "aria-label": "Re-enter Account Number. Enter each digit one by one to confirm."
                                }}
                                onPaste={(e) => {
                                  e.preventDefault();
                                  return false;
                                }}
                                onCopy={(e) => {
                                  e.preventDefault();
                                  return false;
                                }}
                              >
                                Re-enter Account No{" "}
                              </Field>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Field
                                style={{
                                  width: "100%",
                                  color: "#47566A",
                                  fontSize: "16px",
                                  fontWeight: "300",
                                }}
                                component={TextField}
                                id="Bankname"
                                name="Bankname"
                                label="Bank Name"
                              >
                                Bank Name{" "}
                              </Field>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Field
                                style={{
                                  width: "100%",
                                  color: "#47566A",
                                  fontSize: "16px",
                                  fontWeight: "300",
                                }}
                                component={TextField}
                                id="IFSCcode"
                                name="IFSCcode"
                                label="IFSC code"
                                inputProps={{
                                  "aria-label": "IFSC Code. Enter each character one by one."
                                }}
                              >
                                IFSC code{" "}
                              </Field>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Field
                                style={{
                                  width: "100%",
                                  color: "#47566A",
                                  fontSize: "16px",
                                  fontWeight: "300",
                                }}
                                component={TextField}
                                id="BranchName"
                                name="BranchName"
                                label="Branch Name"
                              >
                                Branch Name{" "}
                              </Field>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Field
                                style={{
                                  width: "100%",
                                  color: "#47566A",
                                  fontSize: "16px",
                                  fontWeight: "300",
                                }}
                                component={TextField}
                                id="ACHolderName"
                                name="ACHolderName"
                                label="Account Holder Name"
                              >
                                Account Holder Name{" "}
                              </Field>
                            </Grid>
                          </Grid>

                          <br />
                          <br />

                          <h6 id="upload-document-heading">Upload Document</h6>
                          <div style={{ display: "inline-block" }}>
                            <Box
                              role="note"
                              tabIndex="0"
                              aria-label={`Upload document requirement: ${props.entityid == 11185545 ? "Bank Statement or Cancelled Cheque" : "Check leaf or Bank passbook's front page"}`}
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                marginBottom: 2,
                              }}
                            >
                              <InfoOutlinedIcon
                                aria-hidden="true"
                                sx={{
                                  color: "#1778F2",
                                  fontSize: "20px",
                                  marginRight: 1,
                                  marginTop: "2px",
                                }}
                              />
                              <Typography
                                aria-hidden="true"
                                variant="body2"
                                sx={{
                                  fontSize: "14px",
                                  color: "#47566A",
                                  lineHeight: 1.5,
                                }}
                              >
                                {props.entityid == 11185545
                                  ? "Bank Statement or Cancelled Cheque"
                                  : "Check leaf/ Bank passbook's front page"}
                              </Typography>
                            </Box>
                            <input
                              ref={fileRef}
                              hidden
                              id="file"
                              type="file"
                              onChange={async (e) => {
                                await addBankDocuments(e.target.files);
                                setFieldValue("File", e.target.files[0]);
                              }}
                            />
                            {bankFile.length === 0 ? (
                              <Box
                                role="button"
                                tabIndex="0"
                                aria-label={`Upload bank account proof document. Required: ${props.entityid == 11185545 ? "Bank Statement or Cancelled Cheque" : "Check leaf or Bank passbook's front page"}`}
                                onClick={() => {
                                  fileRef.current.click();
                                }}
                                onKeyDown={handleKeyboardClick(() => fileRef.current.click())}
                                sx={{
                                  border: "1px solid #DBE0E6",
                                  borderRadius: "9px",
                                  padding: "12px",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                  backgroundColor: "#FFFFFF",
                                  width: {
                                    xs: "120px",
                                    sm: "135px",
                                    md: "130px",
                                  },
                                  height: {
                                    xs: "120px",
                                    sm: "135px",
                                    md: "130px",
                                  },
                                  transition: "all 0.2s ease-in-out",
                                  "&:hover": {
                                    borderColor: "#0066DC",
                                    backgroundColor: "#F8FBFF",
                                  },
                                }}
                              >
                                <img
                                  src={uploadIcon}
                                  alt="uploadIcon"
                                  aria-hidden="true"
                                  style={{
                                    width: "30px",
                                    height: "30px",
                                    marginBottom: "8px",
                                    color: "#252D38",
                                  }}
                                />
                                <Typography
                                  aria-hidden="true"
                                  variant="body1"
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    color: "#252D38",
                                    textAlign: "center",
                                  }}
                                >
                                  Upload Document
                                </Typography>
                              </Box>
                            ) : (
                              <BankDocumentPreview
                                bankFile={bankFile}
                                onPreview={handlePreview}
                                onDelete={handleDeleteDocument}
                                DeleteIcon={DeleteIcon}
                              />
                            )}
                            <br></br>
                            <br></br>
                          </div>

                          <br></br>
                          {fileupload ? (
                            <p style={{ marginTop: "6px", color: "#FC1721" }}>
                              {fileupload} &nbsp;
                            </p>
                          ) : (
                            <></>
                          )}
                        </Form>
                      </DialogContent>
                    </Box>
                    <Box
                      sx={{
                        flexShrink: 0,
                        backgroundColor: "#FFFFFF",
                        padding: "16px 20px",
                      }}
                    >
                      <Button
                        startIcon={
                          isSubmitting ? <CircularProgress size="1rem" /> : null
                        }
                        disabled={
                          bankFile.length === 0 ||
                          !isValid ||
                          isSubmitting ||
                          Object.keys(errors).length > 0
                        }
                        variant="contained"
                        onClick={submitForm}
                        fullWidth
                        sx={{
                          maxWidth: "100%",
                          fontWeight: "600",
                          borderRadius: "10px",
                          backgroundColor: "#0066DC",
                          height: "48px",
                        }}
                      >
                        {isSubmitting ? "Processing" : "Save"}
                      </Button>
                    </Box>
                  </>
                )}
              </Formik>
            </div>
          </BootstrapDialog>
        </>
      ) : (
        <></>
      )}

      {autoPayOpen && (
        <AutoPayBankPopup
          open={autoPayOpen}
          handleClose={() => {
              setAutoPayOpen(false);
              setIsIRBank(false); 
          }}
          users={props.users}
          accessToken={props.accessToken}
          loadBankDetails={props.loadBankDetails}
          onAddSuccess={(type) => {
              props.loadBankDetails();
              setAutoPayOpen(false);
              setIsIRBank(false);
              if (type === "pennyDrop") {
                  setSuccessToast({ show: true, message: "Bank Account Saved Successfully!", key: Date.now() });
              } else if (type === "manualDetails") {
                  setSuccessToast({ show: true, message: "Bank account is saved successfully! Our team will verify the bank details shortly.", key: Date.now() });
              }
          }}
          irflag={isIRBank}
          existingBank={isIRBank && bankSelected !== undefined ? props.bank[bankSelected] : null}
        />
      )}
      <BankDocumentPreviewModal
        open={previewModal.open}
        onClose={handleClosePreview}
        previewData={previewModal}
      />
    </>
  );
}

export default BankDetailsForm;
